import { useEffect, useState, type CSSProperties } from 'react';
import type { SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import {
  FACEBOOK_CAPTION_LIMIT,
  FACEBOOK_REEL_CAPTION_LIMIT,
  FACEBOOK_RENDITIONS,
  facebookGridRendition,
  facebookLinkLabel,
} from '../../../../lib/facebook-post-preview';
import { postAuthorName, postLinkUrl, postMediaGrid } from '../../../../lib/social-post-look-parts';
import {
  InlineMediaTile,
  PostAvatar,
  PostCaption,
  PostGrid,
  PostTile,
  PostTiming,
  TallPost,
  useMediaLabel,
  type SocialPostLookProps,
} from './SocialPostLookParts';
import styles from './FacebookPostPreview.module.css';

type MediaShape = { width: number; height: number };

export function FacebookFeedPost({ card, onOpenMedia, onToggleCaption, onOpenLink, onPortrait }: SocialPostLookProps) {
  const label = useMediaLabel(card);
  const grid = postMediaGrid(card);
  const linkUrl = grid.kind === 'none' ? postLinkUrl(card.link) : undefined;
  return (
    <>
      <header className={styles.header}>
        <PostAvatar card={card} name={postAuthorName(card)} size={24} />
        <div className={styles.names}>
          <span className={styles.pageName} data-testid="facebook-page-name">
            <bdi>{postAuthorName(card)}</bdi>
          </span>
          <span className={styles.when} data-testid="facebook-when">
            <PostTiming card={card} />
          </span>
        </div>
      </header>
      {card.caption && (
        <PostCaption
          caption={card.caption}
          className={styles.caption}
          testId="facebook-caption"
          cut={{ limit: FACEBOOK_CAPTION_LIMIT }}
          onToggle={onToggleCaption}
        />
      )}
      {grid.kind === 'single' && grid.tile.kind === 'video' && (
        <FeedVideo
          key={grid.tile.url}
          media={grid.tile}
          label={label(grid.tile, 0)}
          onOpen={() => onOpenMedia(grid.tile, 0)}
          onPortrait={onPortrait}
        />
      )}
      {grid.kind === 'single' && grid.tile.kind === 'image' && (
        <FeedSingleImage key={grid.tile.url} media={grid.tile} label={label(grid.tile, 0)} onOpen={() => onOpenMedia(grid.tile, 0)} />
      )}
      {grid.kind === 'grid' && (
        <PostGrid grid={grid} label={label} rendition={facebookGridRendition} className={styles.media} testId="facebook-media" onOpen={onOpenMedia} />
      )}
      {linkUrl && <FacebookLinkCard link={linkUrl} onOpen={onOpenLink} />}
    </>
  );
}

function FeedSingleImage({ media, label, onOpen }: { media: SocialPostPreviewMedia; label: string; onOpen: () => void }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`${styles.media} ${styles.single}`} data-testid="facebook-media" data-layout="single" data-failed={failed}>
      <PostTile media={media} label={label} rendition={FACEBOOK_RENDITIONS.single} onFail={() => setFailed(true)} onOpen={onOpen} />
    </div>
  );
}

function FeedVideo({
  media,
  label,
  onOpen,
  onPortrait,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  onOpen: () => void;
  onPortrait?: (portrait: boolean) => void;
}) {
  const [shape, setShape] = useState<MediaShape | null>(null);
  const [failed, setFailed] = useState(false);
  const portrait = !!shape && shape.height > shape.width;

  useEffect(() => {
    onPortrait?.(portrait);
    return () => onPortrait?.(false);
  }, [portrait, onPortrait]);

  const box: CSSProperties = { aspectRatio: shape ? `${shape.width} / ${shape.height}` : '16 / 9' };
  return (
    <div className={`${styles.media} ${styles.video}`} style={box} data-testid="facebook-media" data-layout="video" data-failed={failed}>
      <InlineMediaTile
        media={media}
        label={label}
        fit="cover"
        onMeasure={(width, height) => setShape(width > 0 && height > 0 ? { width, height } : null)}
        onFail={() => setFailed(true)}
        onOpen={onOpen}
      />
    </div>
  );
}

function FacebookLinkCard({ link, onOpen }: { link: string; onOpen: () => void }) {
  return (
    <a className={styles.linkCard} href={link} target="_blank" rel="noopener noreferrer" dir="ltr" onClick={onOpen} data-testid="facebook-link-card">
      <span className={styles.linkAddress} data-testid="facebook-link-domain">
        {facebookLinkLabel(link)}
      </span>
      <span className={styles.linkTitle}>{link}</span>
    </a>
  );
}

export function FacebookReel(props: SocialPostLookProps) {
  return (
    <TallPost
      {...props}
      kind="reel"
      name={postAuthorName(props.card)}
      nameTestId="facebook-page-name"
      frameTestId="facebook-reel"
      captionTestId="facebook-caption"
      captionLimit={FACEBOOK_REEL_CAPTION_LIMIT}
    />
  );
}

export function FacebookStory(props: SocialPostLookProps) {
  return <TallPost {...props} kind="story" name={postAuthorName(props.card)} nameTestId="facebook-page-name" frameTestId="facebook-story" />;
}
